import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X, Building2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";
const labelCls = "font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5";

export default function OrganizationsTab() {
  const [orgs, setOrgs] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", type: "", contact_email: "" });
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c] = await Promise.all([api.get("/organizations"), api.get("/admin/users?role=coach")]);
      setOrgs(o.data); setCoaches(c.data);
    } catch { toast.error("Failed to load organizations."); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const nameOf = (id) => coaches.find((c) => c.id === id)?.name || coaches.find((c) => c.id === id)?.email || id;

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: form.name, type: form.type || undefined, contact_email: form.contact_email || undefined };
      await api.post("/organizations", payload);
      toast.success("Organization created."); setForm({ name: "", type: "", contact_email: "" }); setShowAdd(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/organizations/${id}`); toast.success("Deleted."); load(); } catch { toast.error("Delete failed."); } };
  const addCoach = async (id, coachId) => { if (!coachId) return; try { await api.post(`/organizations/${id}/coaches`, { id: coachId }); toast.success("Coach added"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const removeCoach = async (id, coachId) => { try { await api.delete(`/organizations/${id}/coaches/${coachId}`); load(); } catch { toast.error("Failed"); } };

  return (
    <div data-testid="organizations-tab">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl">Organizations</h1>
        <button data-testid="admin-add-org" onClick={() => setShowAdd((v) => !v)} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white/85 transition-colors">
          {showAdd ? <X size={14} /> : <Plus size={14} />} {showAdd ? "Cancel" : "New Organization"}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={create} className="bg-[#121212] border border-white/10 p-6 mb-8 grid sm:grid-cols-3 gap-4" data-testid="org-form">
          <div><label className={labelCls}>Name</label><input data-testid="org-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Type</label><input data-testid="org-type" placeholder="School / Club" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Contact Email</label><input data-testid="org-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className={inputCls} /></div>
          <div className="sm:col-span-3"><button data-testid="org-submit" className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors"><Plus size={14} /> Save</button></div>
        </form>
      )}

      {loading ? <div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div> : orgs.length === 0 ? (
        <div className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="orgs-empty">No organizations yet.</div>
      ) : (
        <div className="space-y-3" data-testid="orgs-list">
          {orgs.map((o) => (
            <div key={o.id} data-testid={`org-${o.id}`} className="bg-[#121212] border border-white/10 p-5">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <Building2 size={20} className="text-white/60" />
                  <div><p className="font-display font-bold uppercase text-xl tracking-tight">{o.name}</p>
                    <p className="text-white/40 text-xs">{[o.type, o.contact_email].filter(Boolean).join(" · ") || "—"} · {(o.coach_ids || []).length} coaches</p></div>
                </div>
                <div className="flex gap-3">
                  <button data-testid={`org-coaches-${o.id}`} onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors">Coaches</button>
                  <button data-testid={`org-delete-${o.id}`} onClick={() => del(o.id)} className="text-white/30 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>
              {expanded === o.id && (
                <div className="mt-5 pt-5 border-t border-white/10" data-testid={`org-coach-panel-${o.id}`}>
                  <label className={labelCls}>Assign Coach</label>
                  <select data-testid={`org-add-coach-${o.id}`} onChange={(e) => { addCoach(o.id, e.target.value); e.target.value = ""; }} defaultValue="" className={`${inputCls} max-w-sm`}>
                    <option value="">Select coach…</option>
                    {coaches.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
                  </select>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {(o.coach_ids || []).map((c) => <button key={c} onClick={() => removeCoach(o.id, c)} className="text-[10px] uppercase tracking-widest border border-white/20 px-2 py-1 hover:bg-red-500/20">{nameOf(c)} ✕</button>)}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
